import { createContext, useContext, useState, useEffect, useRef } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  // Per-tab session isolation: sessionStorage keeps each tab on its OWN
  // account (survives refresh, never leaks between tabs). localStorage only
  // seeds brand-new tabs with the most recent login.
  const [token, setToken] = useState(() => {
    const t = sessionStorage.getItem("token") || localStorage.getItem("token");
    if (t) sessionStorage.setItem("token", t);
    return t;
  });
  const [marketingMode, setMarketingMode] = useState(
    localStorage.getItem("ama-marketing-mode") === "true"
  );

  // Bumped on every session change (login / register / logout / token mutation).
  // Any in-flight /auth/me whose `myId` no longer matches this counter is
  // a stale response from a previous session and MUST be discarded. Without
  // this guard, a slow /auth/me from a previous token can resolve AFTER a
  // login(), overwriting the freshly-logged-in user with the previous one —
  // the bug that made test-account logins appear to "become" the admin.
  const sessionIdRef = useRef(0);

  // Toggle marketing mode (admin only)
  const toggleMarketingMode = () => {
    const newValue = !marketingMode;
    setMarketingMode(newValue);
    localStorage.setItem("ama-marketing-mode", String(newValue));
  };

  // Get display tier (shows Platinum when marketing mode is on for Ultimate users)
  const getDisplayTier = () => {
    if (marketingMode && user?.subscription_tier === "ultimate") {
      return "platinum";
    }
    return user?.subscription_tier || "free";
  };

  // Get display limits for marketing mode
  const getDisplayLimits = () => {
    if (marketingMode && user?.subscription_tier === "ultimate") {
      return {
        days_between_videos: 1
      };
    }
    return null; // Use actual stats from API
  };

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchUser = async () => {
    const myId = sessionIdRef.current;
    try {
      const response = await axios.get(`${API}/auth/me`);
      // Discard if a newer session has started while we were in flight.
      if (myId !== sessionIdRef.current) return;
      setUser(response.data);
    } catch (error) {
      if (myId !== sessionIdRef.current) return;
      console.error("Failed to fetch user:", error);
      logout();
    } finally {
      if (myId === sessionIdRef.current) setLoading(false);
    }
  };

  const login = async (email, password) => {
    // Wipe any previous-session axios header BEFORE the POST so a stale
    // Authorization can't leak into the request. Also invalidate any in-flight
    // /auth/me from the previous session.
    sessionIdRef.current += 1;
    delete axios.defaults.headers.common["Authorization"];

    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { token: newToken, user: userData } = response.data;

    sessionIdRef.current += 1;  // new session starts now
    sessionStorage.setItem("token", newToken);
    localStorage.setItem("token", newToken);
    axios.defaults.headers.common["Authorization"] = `Bearer ${newToken}`;
    setToken(newToken);
    setUser(userData);
    return userData;
  };

  const register = async (email, password, name, referralCode = null) => {
    sessionIdRef.current += 1;
    delete axios.defaults.headers.common["Authorization"];

    const payload = { email, password, name };
    if (referralCode) {
      payload.referral_code = referralCode;
    }
    const response = await axios.post(`${API}/auth/register`, payload);
    const { token: newToken, user: userData, referral_bonus } = response.data;

    sessionIdRef.current += 1;
    sessionStorage.setItem("token", newToken);
    localStorage.setItem("token", newToken);
    axios.defaults.headers.common["Authorization"] = `Bearer ${newToken}`;
    setToken(newToken);
    setUser(userData);
    return { ...userData, referral_bonus };
  };

  const logout = () => {
    sessionIdRef.current += 1;
    sessionStorage.removeItem("token");
    // Only clear the shared "last login" if it's THIS tab's account — a
    // logout in one tab must not nuke a different account's seed token.
    if (localStorage.getItem("token") === token) {
      localStorage.removeItem("token");
    }
    delete axios.defaults.headers.common["Authorization"];
    setToken(null);
    setUser(null);
  };

  const refreshUser = async () => {
    await fetchUser();
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      token,
      loading, 
      login, 
      register, 
      logout, 
      refreshUser,
      marketingMode,
      toggleMarketingMode,
      getDisplayTier,
      getDisplayLimits
    }}>
      {children}
    </AuthContext.Provider>
  );
};
